import React from 'react';
import {
  Button, Card, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PageHeader from '../components/PageHeader';
import StateBoundary from '../components/StateBoundary';
import { useActivityViewModel } from '../viewmodels/ActivityViewModel';
import { formatDateTime } from '../utils/format';

const EVENT_LABEL = {
  'auth.signin': 'Signed in',
  'auth.signin_failed': 'Failed sign-in attempt',
  'auth.signup': 'Account created',
  'auth.password_reset': 'Password reset',
  'connection.saved': 'Connection saved',
  'connection.deleted': 'Connection removed',
  'connection.denied': 'Connection change blocked',
  'access.denied': 'Action blocked (owner only)',
  'team.created': 'Team created',
  'goal.created': 'Goal given',
  'run.started': 'Run started',
  'run.completed': 'Run completed',
  'run.stopped': 'Run stopped early',
  'run.failed': 'Run failed',
  'tool.called': 'Team member read store data',
  'tool.denied': 'Team member blocked from a tool',
  'approval.created': 'Proposal made',
  'approval.approved': 'Request approved',
  'approval.executed': 'Approved request carried out',
  'approval.rejected': 'Request rejected',
  'approval.failed': 'Approved request could not be completed',
  'bot.created': 'Team member added',
  'bot.updated': 'Team member changed',
  'message.posted': 'You sent a message',
  'message.blocked': 'Team member message blocked (not allowed by the hierarchy)',
  'run.stop_requested': 'You stopped the team',
  'settings.updated': 'Permission changed in Settings',
  'task.created': 'Task added',
  'task.updated': 'Task changed',
  'trigger.created': 'Trigger created',
  'trigger.updated': 'Trigger changed',
  'trigger.deleted': 'Trigger deleted',
  'schedule.created': 'Schedule created',
  'schedule.updated': 'Schedule changed',
  'schedule.deleted': 'Schedule deleted',
  'schedule.fired': 'Scheduled message sent to the team',
};

function who(item) {
  if (item.actor_type === 'bot') return item.meta?.bot || 'Team member';
  if (item.actor_type === 'system') return 'System';
  return 'You or your team';
}

function detail(item) {
  const m = item.meta || {};
  if (m.stop_reason) return `Reason: ${m.stop_reason}`;
  if (m.to) return `To ${m.to}`;
  if (m.tool) return m.tool;
  if (m.provider) return m.provider;
  return '';
}

export default function ActivityView() {
  const vm = useActivityViewModel();
  return (
    <>
      <PageHeader title="Activity" subtitle="Everything your team and your account did, newest first." />
      <StateBoundary
        loading={vm.loading}
        error={vm.error}
        onRetry={vm.reload}
        isEmpty={vm.items.length === 0}
        empty={{ icon: <HistoryOutlinedIcon fontSize="large" />, title: 'No activity yet', body: 'Actions will be recorded here.' }}
        rows={6}
      >
        <Card sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>What</TableCell>
                <TableCell>Who</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vm.items.map((a) => (
                <TableRow key={a.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(a.created_at)}</TableCell>
                  <TableCell>{EVENT_LABEL[a.event_type] || a.event_type}</TableCell>
                  <TableCell>{who(a)}</TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{detail(a)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        {vm.hasMore && (
          <Stack alignItems="center" sx={{ mt: 2 }}>
            <Button onClick={vm.loadMore} disabled={vm.loadingMore}>{vm.loadingMore ? 'Loading...' : 'Load older'}</Button>
          </Stack>
        )}
      </StateBoundary>
    </>
  );
}
